import BaseActionAbstract from 'cio/app-models/action-abstracts/base';
import { task } from 'ember-concurrency';
import { differentFilters } from 'cio/utils/filters';
import { computed } from '@ember/object';
import { edgesEndingInExitCount, retrieveOutgoingEdges } from 'cio/utils/visual-workflow-builder/branching';

const EDITABLE_PROPERTIES = ['name'];
const DIRTY_DEPENDENCIES = `action.{${EDITABLE_PROPERTIES.join(',')}}`;

export default BaseActionAbstract.extend({
  type: 'conditional_split',

  action: null,
  actionType: null,

  // To be cleaned up during conversion to native JS classes:
  // eslint-disable-next-line ember/avoid-leaking-state-in-ember-objects
  preconditions: [],

  conditions: null,

  canDelete: false,
  canDeleteNotice: null,

  isOldVWB: false,

  _editableProperties: EDITABLE_PROPERTIES,
  _dirtyDependencies: DIRTY_DEPENDENCIES,

  prepareForEdit() {
    this._super(...arguments);

    let canDelete = true;
    let canDeleteNotice = null;

    if (this.isOldVWB) {
      let branchReconnectPoint = this.action.campaign.branchRejoinPointsMap[this.action.id];
      let { traversedNodes, leafNodes } = retrieveOutgoingEdges(this.action, {
        edges: this.action.campaign.edges,
        reconnectNodeId: branchReconnectPoint,
      });
      // Traversed nodes includes the branch node we're traversing, so we're
      // checking for length of 1
      let isBranchEmpty = traversedNodes.length === 1 && leafNodes.length === 0;

      if (!isBranchEmpty) {
        canDelete = false;

        // Traversed nodes also include the leaf nodes.
        // If traversed nodes is 3 (this branch plus two exits), and there are two
        // exits, let's prompt to reconnect the exits before deleting.
        // Ideally we should be able to handle deleting a branch that only has
        // exits, but that requires jumping through extra hoops, so let's go with
        // this for now.
        if (traversedNodes.length === 3 && leafNodes.length === 2) {
          canDeleteNotice = 'Please reconnect all exits before deleting';
        } else {
          canDeleteNotice = 'Please remove all actions from within this branch before deleting';
        }
      }
    } else {
      let treeNode = this.action.campaign.branchesMap[this.action.id];
      canDelete = edgesEndingInExitCount(treeNode) !== treeNode.edges.length;

      if (!canDelete) {
        // If all edges of this branch action end in exts, prompt to reconnect the
        // exits before deleting.
        // Ideally we should be able to handle deleting a branch that only has
        // exits, but that requires jumping through extra hoops, so let's go with
        // this for now.
        canDeleteNotice = 'Please reconnect all exits before deleting';
      }
    }

    this.setProperties({
      conditions: [...this.action.conditions],
      canDelete,
      canDeleteNotice,
    });
  },

  prepareForSave() {
    let action = this._super(...arguments);

    action.set('conditions', this.conditions);

    return action;
  },

  _isDirty: computed('conditions.[]', 'action.conditions.[]', function () {
    return differentFilters(this.conditions, this.action.conditions);
  }),

  // eslint-disable-next-line require-yield
  saveTask: task(function* () {
    let action = this.prepareForSave();

    try {
      let response = yield action.save();
      this.prepareForEdit();
      return response;
    } catch (e) {
      action.rollbackAttributes();
      throw e;
    }
  }).drop(),

  // eslint-disable-next-line require-yield
  deleteTask: task(function* () {
    return this.action.campaign.removeActionFromWorkflow(this.action);
    // return yield this.action.deleteViaAPI();
  }).drop(),
});
